import type { Installation } from '../types/auth';

/**
 * 挂件在商家店面上到底开着没有。
 *
 * <p><b>这个文件存在的全部理由：`status` 回答不了这个问题。</b>
 * `status` 上的 `inactive` 由后端心跳按「`updated_at` 超过 18 小时没动」写，
 * 而推进 `updated_at` 的主要是**访客打开店面挂件**。所以它读作
 * 「这家店的店面最近有没有访客」—— prod 2026-09-19：1,059 个在装商家里
 * 990 个（93.5%）是 `inactive`，其中大量是挂件好好开着、只是那天没人逛的小店。
 *
 * <p>更彻底的一个数：即便只看 `status='active'` 的 41 行，按分钟看
 * 17 行的 `updated_at` 落在巡检自己那一分钟上 —— 它们在两轮巡检之间
 * 同样没有任何访客流量。
 *
 * <p>正解是 `switchStatus`：后端每 12 小时读一次已发布主题
 * `config/settings_data.json` 里那个 app embed block 的 `disabled`
 * （Shopify 没有 asset 级 webhook，轮询是官方唯一路径）。
 * 见 chatbot-api `Installation#switchStatus` 的 javadoc。
 *
 * <p>而这个答案**什么时候问出来的**，由 `switchStatusCheckedAt` 单独回答
 * （chatbot-api #2201 / #2202）。第一版拿 `updated_at` 当新鲜度代理，
 * 那是个只在一个方向上成立的代理；现在不用猜了，见 `isFrozen`。
 */

/** 挂件状态的三种答案。`unknown` 是**真的不知道**，不是「关着」。 */
export type WidgetState = 'on' | 'off' | 'unknown';

/**
 * 这一次检查多久之后就不再算数。**这是一条新鲜度策略，不是后端常量的副本。**
 *
 * <p>锚在巡检的**节奏**上：它每 12 小时跑一轮，所以一行健康的记录，
 * `switchStatusCheckedAt` 最多 12 小时旧。72 小时 = 连着错过六轮，足够宽松到
 * 不误伤偶发的抓取失败，又不至于替一个两周前的答案背书。
 *
 * <p>⚠️ 不要改成 30 天去抄后端的 `GIVE_UP_AFTER`：**淘汰门槛不是新鲜度门槛**。
 * 那是「这行还值不值得再问」，本常量是「上次问到的还算不算数」——
 * 抄过来等于替最多 60 轮之前的答案背书。
 */
const STALE_AFTER_MS = 72 * 60 * 60 * 1000;

/**
 * ⚠️ 只有 `'true'` 算开着，其余一律不算。
 *
 * 不要写成 `!== 'false'`：这一列的取值空间不止 true/false/null ——
 * test 库 554 行里有 320 行是 `'OFF'`（来源不明，不是巡检写的）。
 */
function readSwitch(raw: string | null | undefined): WidgetState {
  if (raw === 'true') return 'on';
  if (raw === 'false') return 'off';
  return 'unknown';
}

/**
 * 读一行安装记录的挂件状态。
 *
 * <p>四条规则，顺序有意义：
 *
 * ⚠️ `status` 的取值空间是 active / inactive / installed / uninstalled ——
 * prod 上 26 行是 `installed`（全部 `switchStatus='false'`）。新旧判据都把它
 * 当成「不算开着」，所以对它们没有影响。
 *
 * 1. **先过卸载。** 后端只做增量清空、**没有回填**，库里还存着 200 行
 *    `status='uninstalled'` 却带着值的行，其中 75 行是 `'true'`
 *    （prod 2026-09-19）。不先过就读，会告诉这 75 个已经卸载的商家
 *    「你的挂件开着」。
 *    ⚠️ 用 `status`（本接口带它）而不是 `connection_state`（本接口不带，
 *    它住在 `/store/connection` 那个单独的接口上）；今天两道闸选出的是同一批行。
 * 2. **只有 shopify 行有这一列。** 手动安装（platform 为空或 `manual`）
 *    没有 Shopify 主题可读，永远 `unknown`。
 * 3. **`unknown` 要回退，不要当成「没装」。** 覆盖率在巡检批次里是 203/221
 *    ≈ 92%；拿不到的那些交给调用方自己决定怎么退。
 * 4. **超过 72 小时没被巡检问过的值不算数**（见 `isFrozen` 与 `STALE_AFTER_MS`）。
 *    ⚠️ 这里问的是「上次**检查**是什么时候」，不是「这行上次**被写**是什么时候」——
 *    后者会被访客流量、时区更新、连接状态变更一起推着走，见 `isFrozen`。
 */
export function readWidgetState(installation: Installation | null | undefined): WidgetState {
  if (!installation) return 'unknown';
  if (installation.status === 'uninstalled') return 'off';
  if (installation.platform !== 'shopify') return 'unknown';
  if (isFrozen(installation)) return 'unknown';
  return readSwitch(installation.switchStatus);
}

/**
 * 🔴 **值存在 ≠ 值还算数。**
 *
 * <p>「拿不到真值就回退，所以不会比以前更差」——这句话只覆盖了**值缺失**。
 * 还有一类是**值冻结**：那一行早就被移出巡检批次，最后一次回答被原样留着。
 * prod 2026-09-20 实测，`status='inactive'` + `switchStatus='true'` 且超过 30 天
 * 没被刷新的有 **94 行**（占在装 shopify 行的 8.9%），最老的停在 2025-12-05。
 * 不拦的话这 94 家店今天显示 Inactive、改完显示 Active，而 `AnnaOnboardingCards`
 * 会替我们说出「我已经在你店面的挂件上跑着了」。
 *
 * <p>后端接受「陈旧的真好过确定的假」（见 chatbot-api #2189），那是为了不主动
 * 说错话；而前端是**把它讲出口**的一侧，标准不一样。超窗就退回 `status`。
 *
 * <p><b>判据是 `switchStatusCheckedAt`，不是 `updatedAt`。</b>
 * 后端只有 `updateSwitchStatus` 会盖这个章，`clearSwitchStatus*` 把它和值一起清成
 * null（chatbot-api #2201/#2202）—— 也就是说它**只**在「真的问过一次主题」时动。
 * 第一版用的 `updated_at` 是个只在一个方向上成立的代理：成功的巡检写回确实会推它，
 * 但反过来不成立，`touchUpdatedAt`（200 但读不出结果时**刻意**只推时间、不写值，
 * 见 #2189）、`updateTimezone`、`updateConnectionState`、`reactivate`，以及访客每次
 * 打开挂件时的 `ExternalStartService#refreshUpdatedAt` 都在推同一列。
 * 所以那一版有个**没有上限**的洞：一家每轮都「200 但读不懂」的店，`updated_at`
 * 永远不到 12 小时，而 `switchStatus` 一动不动，本规则对它永远不触发。这一版没有这个洞。
 *
 * <p>🔴 **读不出时间 = 判冻结**（fail closed）。本文件里其余每一个答不上来的问题
 * 都落到 unknown → 回退 → 旧行为，这里也必须一样 —— 落到「把那个值当成当下事实
 * 讲出去」的才是要拦的东西。
 *
 * <p>⚠️ **切过来的那一刻，线上每一行都走这条 fail-closed 分支。**
 * `switch_status_checked_at` 是新列且**刻意没有回填**（prod `databasechangelog` 有
 * 91 个版本前缀，回填会随每次版本号变更重跑），所以在 #2202 部署 + 巡检跑过之前，
 * 每一行的这个字段都缺失 → 每一行 shopify 都是 `unknown` → `isWidgetLive` 退回
 * `status === 'active'`，**正好是 #1636 之前的行为**。批次里那 221 行会在 ≤12 小时内
 * 自愈；已经被后端永久淘汰的那 330 行不会 —— 它们本来就不该再被背书。
 */
function isFrozen(installation: Installation): boolean {
  const ts = Date.parse(installation.switchStatusCheckedAt ?? '');
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > STALE_AFTER_MS;
}

/**
 * 「挂件在店面上跑着吗」——给只需要一个布尔值的调用方。
 *
 * <p>`unknown` 时回退到旧判据 `status === 'active'`。**那个判据是错的**
 * （见文件头），但在拿不到真值时它仍然是手上最好的信号 —— 拿不到答案时
 * 不该比以前更差。
 *
 * <p>手动安装永远走这条回退路：它们没有 `switchStatus`。
 */
export function isWidgetLive(installation: Installation | null | undefined): boolean {
  const state = readWidgetState(installation);
  if (state !== 'unknown') return state === 'on';
  return installation?.status === 'active';
}
